import { useTranslation } from '../../core/hooks/use-translation';
import type { TranslationKey } from '../../core/i18n/translation';

const STEPS: readonly TranslationKey[] = [
    'booking.step.service',
    'booking.step.specialist',
    'booking.step.day',
    'booking.step.details'
];

/** Which step the calendar is, and so which one an online request strikes out. */
const SLOT_STEP = 3;

interface BookingStepperProps {
    /** 1 to 4. */
    current: number;
    /** An online request picks no time, so the calendar step is struck out rather than removed. */
    skipSlot: boolean;
}

/**
 * The four-step rail above the card.
 *
 * **A skipped step is shown, not removed.** An online request never picks a day, and the live page
 * strikes that step through and marks its circle with a dash — which tells the customer the wizard
 * is now two steps long *and why*. Dropping it renumbers the form under them instead, so somebody
 * who switches back to an in-person meeting sees a step reappear with no explanation.
 *
 * It is an ordered list with `aria-current`, so the same three facts a sighted customer reads off
 * the ticks reach a screen reader: which step this is, which are behind, and which does not apply.
 */
export function BookingStepper({ current, skipSlot }: BookingStepperProps) {
    const { t } = useTranslation();

    return (
        <ol className="stepper" data-reveal="step" aria-label={t('booking.progress')}>
            {STEPS.map((label, index) => {
                const number = index + 1;
                const skipped = number === SLOT_STEP && skipSlot;
                const done = !skipped && number < current;
                const active = number === current;

                return (
                    <li
                        key={label}
                        className={`step-item${active ? ' active' : ''}${done ? ' done' : ''}${skipped ? ' skipped' : ''}`}
                        aria-current={active ? 'step' : undefined}
                        aria-disabled={skipped ? true : undefined}
                    >
                        <span className="step-circle" aria-hidden="true">
                            {skipped ? '—' : done ? '✓' : number}
                        </span>
                        <span className="step-label">{t(label)}</span>
                    </li>
                );
            })}
        </ol>
    );
}
